import { AppContext } from '@/contexts/app.context';
import { BodyZone, MuscleTag } from '@/generated';
import {
  Typography,
  Box,
  TextField,
  Button,
  Stack,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  MenuItem,
  Select,
  Avatar,
} from '@mui/material';
import { useContext, useEffect, useReducer, useState } from 'react';
import { MuscleTagIdList } from './MuscleTagIdList';
import { enqueueSnackbar } from 'notistack';
import { muscleTagFilter } from '@/utils/muscle-tag-filter';
import { MuscleTagEditor } from './muscle-tags-tab/MuscleTagEditor';
import { isAdminUser } from '@/utils/user';
import { filterBodyzones } from '@/utils/bodyzone-filter';
import { ImageCropComponent } from '@/components/images/ImageCropComponent';
import { FileInputButton } from '@/components/FileInput';
import { Photo } from '@mui/icons-material';
import { ImageFromServer } from '@/components/images/ImageFromServer';
import { useBodyZoneListQuery, useMuscleTagsQuery } from '@/hooks/query-hooks';
import { useCreateBodyZone, useDeleteBodyZone, useUpdateBodyZone } from '@/hooks/body-zone-mutation.hooks';
import { useCreateMuscleTag } from '@/hooks/muscle-tag-mutation.hook';
import { uploadFile } from '@/utils/api-helpers';

interface TagListState {
  muscleTagList: Array<MuscleTag>;
  muscleTagMap: Map<string, MuscleTag>;
}

interface TagAction {
  type: 'init' | 'add' | 'edit' | 'delete';
  payload: any;
}

const tagListReducer = (state: TagListState, action: TagAction): TagListState => {
  switch (action.type) {
    case 'init':
      return action.payload;
    case 'add':
      return { muscleTagList: [...state.muscleTagList, action.payload], muscleTagMap: state.muscleTagMap.set(action.payload.id, action.payload) };
    case 'edit':
      return {
        muscleTagList: state.muscleTagList.map((bzt) => (bzt.id === action.payload.id ? action.payload : bzt)),
        muscleTagMap: state.muscleTagMap?.set(action.payload.id, action.payload),
      };
    case 'delete':
      state.muscleTagMap.delete(action.payload.id);
      return { muscleTagList: state.muscleTagList.filter((bzt) => bzt.id !== action.payload.id), muscleTagMap: state.muscleTagMap };
    default:
      return state;
  }
};

const noZone: BodyZone = { id: 'nz', isDefaultZone: false, name: 'None', zoneTagIds: [] };

export const BodyZonesTab = () => {
  const { state: contextState } = useContext(AppContext);
  const [state, dispatch] = useReducer(tagListReducer, { muscleTagList: [], muscleTagMap: new Map<string, MuscleTag>() });
  const [newBodyZoneName, setNewBodyZoneName] = useState<string>();
  const [selectedTag, setSelectedTag] = useState<MuscleTag>();
  const [filterName, setFilterName] = useState<string>('');
  const [selectedBodyZone, setSelectedBodyZone] = useState<BodyZone>(noZone);
  const { bodyZoneList } = useBodyZoneListQuery();
  const { muscleTags, muscleTagMap } = useMuscleTagsQuery();
  const createBodyZone = useCreateBodyZone();
  const createMuscleTag = useCreateMuscleTag();

  useEffect(() => {
    dispatch({ type: 'init', payload: { muscleTagList: muscleTags, muscleTagMap } });
  }, [muscleTags, muscleTagMap]);

  return (
    <>
      {isAdminUser(contextState?.userProfile) && (
        <>
          <Typography sx={{ m: 2 }} variant="h6">
            Muscle Tags
          </Typography>
          <Grid gap={2} container>
            <Grid size={2} sx={{}}>
              <TextField sx={{ my: 1 }} label="Filter" autoFocus={true} value={filterName} onChange={(e: any) => setFilterName(e.target.value)} />
              <Select value={selectedBodyZone.id}>
                {[noZone, ...filterBodyzones(bodyZoneList ?? [])].map((bz) => (
                  <MenuItem
                    key={bz.id}
                    value={bz.id}
                    selected={selectedBodyZone.id === bz.id}
                    onClick={(e) => {
                      setSelectedBodyZone(bz);
                    }}
                  >
                    {bz.name}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid size={9}>
              {state.muscleTagList ? (
                <MuscleTagEditor
                  tagEditMode={false}
                  tagClicked={(tag) => {
                    setSelectedTag(tag);
                  }}
                  tagCreated={async (tagName: string) => {
                    const newTag = await createMuscleTag.mutateAsync({ name: tagName });
                    dispatch({ type: 'add', payload: newTag });
                  }}
                  tagRemoved={() => {}}
                  tagUpdated={() => {}}
                  muscleTags={muscleTagFilter(state.muscleTagList, filterName, selectedBodyZone)}
                />
              ) : undefined}
            </Grid>
          </Grid>
        </>
      )}
      <Typography variant="h4" style={{ color: 'white', marginLeft: 10, marginTop: 20 }}>
        Body Zones:
      </Typography>
      <Stack sx={{ m: 1 }}>
        {bodyZoneList?.map((bz, i) => (
          <Box sx={{ borderColor: 'rgb(96, 108, 56)', borderWidth: 1, borderStyle: 'dashed', borderRadius: 3, padding: 1, my: 1 }}>
            <BodyZoneElement
              editModeChanged={(editMode) => {
                setSelectedTag(undefined);
              }}
              bodyZoneSaved={(ubz) => {}}
              bodyZoneDeleted={(dbz) => {}}
              bodyZone={bz}
              muscleTagMap={muscleTagMap}
              selectedMuscleTag={selectedTag}
            />
          </Box>
        ))}
        {isAdminUser(contextState?.userProfile) && (
          <Box
            sx={{
              borderColor: 'rgb(96, 108, 56)',
              borderWidth: 1,
              borderStyle: 'dashed',
              borderRadius: 3,
              padding: 3,
              my: 1,
              display: 'flex',
              gap: 3,
            }}
          >
            <Button
              onClick={async () => {
                if (newBodyZoneName) {
                  await createBodyZone.mutateAsync({ isDefaultZone: false, name: newBodyZoneName, zoneTagIds: [] });
                }
                setNewBodyZoneName('');
              }}
              style={{ color: 'white' }}
            >
              Create new Zone
            </Button>
            <TextField
              label="Zone Name"
              sx={{ width: 400 }}
              value={newBodyZoneName}
              onChange={(e) => {
                setNewBodyZoneName(e.target.value);
              }}
            ></TextField>
          </Box>
        )}
      </Stack>
    </>
  );
};

interface BodyZoneProps {
  bodyZone: BodyZone;
  muscleTagMap?: Map<string, MuscleTag>;
  selectedMuscleTag?: MuscleTag;
  bodyZoneSaved: (bodyZone: BodyZone) => void;
  bodyZoneDeleted: (bodyZone: BodyZone) => void;
  editModeChanged: (editMode: boolean) => void;
}

const BodyZoneElement = (props: BodyZoneProps) => {
  const { bodyZone } = props;
  const [editMode, setEditMode] = useState(false);
  const [bodyZoneName, setBodyZoneName] = useState(props.bodyZone.name);
  const [muscleTagIds, setMuscleTagIds] = useState(props.bodyZone.zoneTagIds);
  const [isDefaultZone, setIsDefaultZone] = useState(props.bodyZone.isDefaultZone);
  const [imageFile, setImageFile] = useState<File | null | undefined>();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const { state } = useContext(AppContext);
  const updateBodyZone = useUpdateBodyZone();
  const wh = 135;

  useEffect(() => {
    if (editMode && props.selectedMuscleTag && !muscleTagIds.some((bzTagId) => bzTagId === props.selectedMuscleTag?.id))
      setMuscleTagIds([...muscleTagIds, props.selectedMuscleTag?.id]);
    else if (editMode && props.selectedMuscleTag && muscleTagIds.some((bzTagId) => bzTagId === props.selectedMuscleTag?.id))
      setMuscleTagIds(muscleTagIds.filter((tagId) => props.selectedMuscleTag?.id !== tagId));
  }, [props.selectedMuscleTag]);

  return (
    <Box>
      {editMode ? (
        <>
          <Box style={{}}>
            <TextField
              style={{ margin: 10 }}
              type="text"
              value={bodyZoneName}
              onChange={(v) => {
                setBodyZoneName(v.target.value);
              }}
            />
          </Box>
          <Box sx={{ marginTop: 2, marginBottom: 2 }}>
            <ImageCropComponent imageFile={imageFile} onImageFileChanged={setImageFile} imageId={bodyZone.imageId} />
          </Box>
          <Box sx={{ mt: 0, mb: 3, columnGap: 2, display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
            <FileInputButton accept=".png,.jpg,.jpeg" startIcon={<Photo />} onFilePicked={(f) => setImageFile(f)}>
              Change Image
            </FileInputButton>
          </Box>
          <Box>
            <MuscleTagIdList muscleTagIds={muscleTagIds} muscleTagMap={props.muscleTagMap} />
          </Box>
          <Box>
            <FormGroup style={{ background: 'black' }}>
              <FormControlLabel control={<Checkbox checked={isDefaultZone} onChange={(e) => setIsDefaultZone(!isDefaultZone)} />} label="Default" />
            </FormGroup>
          </Box>
        </>
      ) : (
        <Stack direction={'row'} sx={{}}>
          <Stack>
            <Avatar sx={{ width: wh, height: wh, m: 1, backgroundColor: 'white' }}>
              <ImageFromServer sx={{ maxWidth: wh, maxHeight: wh }} thumbnail={true} alt="" id={bodyZone?.imageId} />
            </Avatar>
            <Box sx={{ m: 1, textAlign: 'center' }}>
              <Typography variant="h6" style={{ color: 'white', marginTop: 10 }}>
                {props.bodyZone.name}
              </Typography>
            </Box>
          </Stack>
          <Box sx={{ m: 1 }}>
            <MuscleTagIdList sx={{ backgroundColor: 'rgb(96, 108, 56)' }} muscleTagIds={props.bodyZone.zoneTagIds} muscleTagMap={props.muscleTagMap} />
          </Box>
        </Stack>
      )}
      {isAdminUser(state?.userProfile) && (
        <Box sx={{ m: 1, display: 'flex', justifyContent: 'flex-end' }}>
          {editMode ? (
            <Button
              style={{ color: 'white' }}
              onClick={async () => {
                setEditMode(!editMode);
                props.editModeChanged(editMode);
                let bz: BodyZone = { isDefaultZone, name: bodyZoneName, id: props.bodyZone.id, zoneTagIds: muscleTagIds };
                if (imageFile) {
                  const fileId = await uploadFile(imageFile);
                  bz = { ...bz, imageId: fileId };
                }
                await updateBodyZone.mutateAsync(bz);
                props.bodyZoneSaved({ isDefaultZone, name: bodyZoneName, id: props.bodyZone.id, zoneTagIds: muscleTagIds });
              }}
            >
              Save
            </Button>
          ) : (
            <Button
              style={{ color: 'white' }}
              onClick={() => {
                setEditMode(!editMode);
                props.editModeChanged(editMode);
              }}
            >
              Edit
            </Button>
          )}
          <Button
            onClick={async () => {
              setShowDeleteDialog(true);
            }}
            style={{ color: 'white' }}
            className="m-3"
          >
            Delete
          </Button>
        </Box>
      )}

      <DeleteBodyZoneDialog
        bodyZone={props.bodyZone}
        setOpen={() => {
          setShowDeleteDialog(!showDeleteDialog);
        }}
        open={showDeleteDialog}
      />
    </Box>
  );
};

function DeleteBodyZoneDialog(props: { bodyZone: BodyZone; open: boolean; setOpen: (open: boolean) => void }) {
  const { open, setOpen, bodyZone } = props;
  // const context = useContext(AppContext);
  const deleteBodyZone = useDeleteBodyZone();

  return (
    <Dialog open={open} onClose={() => setOpen(false)} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
      <DialogTitle id="alert-dialog-title">{'Really delete?'}</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">Really delete this Body Zone?</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={async () => {
            await deleteBodyZone.mutateAsync(bodyZone.id);
            enqueueSnackbar('Body Zone deleted!');
          }}
          color="primary"
          autoFocus
        >
          Yes
        </Button>
        <Button onClick={() => setOpen(false)} color="primary">
          No
        </Button>
      </DialogActions>
    </Dialog>
  );
}
